// Central route metadata: title + back-target + landing detection.
// Pattern-matched top-down; use ":x" for URL params.

const META = [
  // ---------- Role landings (no back button) ----------
  ["/", { title: "Sites", landing: true }],
  ["/command", { title: "Command Centre", landing: true }],
  ["/client", { title: "My Site", landing: true }],
  ["/procurement", { title: "Procurement", landing: true }],
  ["/home", { title: "Home", landing: true }],

  // ---------- Site-scoped pages ----------
  ["/sites/:id/run", { title: "Daily Checklist" }],
  ["/sites/:id/runs/:runId", { title: "Checklist Run" }],
  ["/sites/:id/tracker", { title: "Monthly Tracker" }],
  ["/sites/:id/attendance", { title: "Attendance" }],
  ["/sites/:id/attendance/month", { title: "Monthly Attendance", backTo: (p) => backOneLevel(p) }],
  ["/sites/:id/roster", { title: "Roster" }],
  ["/sites/:id/consumables", { title: "Consumables" }],
  ["/sites/:id/reconciliation", { title: "Consumables Reconciliation" }],
  ["/sites/:id/purchase", { title: "Purchase Requests" }],
  ["/sites/:id/usage", { title: "Usage Logs" }],
  ["/sites/:id/documents", { title: "Site Documents" }],
  ["/sites/:id/diary", { title: "Supervisor Diary" }],
  ["/sites/:id/expenses", { title: "Expenses" }],
  ["/sites/:id/expenses/report", { title: "Monthly Expense Report", backTo: (p) => p.replace(/\/report$/, "") }],
  ["/sites/:id/billable-log", { title: "Billable Log" }],
  ["/sites/:id/billing-statement", { title: "Billing Statement" }],
  ["/sites/:id/requisition", { title: "Site Requisition" }],
  ["/sites/:id/issue-log", { title: "Consumables Issue Log" }],
  ["/buildings/:id", { title: "Building Operations" }],
  ["/coworking/:id", { title: "Coworking Sales & Cash" }],

  // ---------- Global cross-site pages ----------
  ["/sla", { title: "SLA Lookup" }],
  ["/sla/:id", { title: "SLA Lookup" }],
  ["/service-requests", { title: "Escalations" }],
  ["/service-requests/:id", { title: "Escalation", backTo: () => "/service-requests" }],
  ["/compliance", { title: "Compliance" }],
  ["/dashboard", { title: "Ops Dashboard" }],
  ["/trends", { title: "Trends" }],
  ["/documents", { title: "Documents" }],
  ["/messages", { title: "Messages" }],
  ["/announcements", { title: "Announcement Whiteboard" }],
  ["/training", { title: "Training" }],
  ["/training/:id", { title: "Course", backTo: () => "/training" }],
  ["/coaching", { title: "Coaching Queue" }],
  ["/kra", { title: "KRA Scorecard" }],
  ["/diary", { title: "Diary" }],
  ["/change-password", { title: "Change Password" }],

  // ---------- Mini-HR + Readiness + Tutorial ----------
  ["/leaves", { title: "Leaves" }],
  ["/loans", { title: "Loans" }],
  ["/readiness", { title: "Go-live Readiness Board" }],
  ["/tutorial", { title: "30-day Tutorial" }],
  ["/staff/:id", { title: "Staff Profile" }],
  ["/ot", { title: "Overtime" }],
  ["/reconciliation", { title: "Agency Reconciliation" }],
  ["/assets", { title: "Asset Register" }],
  ["/assets/:id", { title: "Asset", backTo: () => "/assets" }],
  ["/handovers", { title: "Handovers" }],
  ["/visitors", { title: "Visitors" }],

  // ---------- Admin ----------
  ["/admin/users", { title: "Users" }],
  ["/admin/templates", { title: "Template Editor" }],
  ["/admin/presets", { title: "Preset Manager" }],
  ["/admin/sla-editor", { title: "SLA Editor" }],
  ["/admin/catalog", { title: "Consumables Catalog" }],
  ["/admin/absence-rules", { title: "Absence Rules" }],
  ["/admin/storage", { title: "Storage" }],
  ["/admin/outbox", { title: "Email Outbox" }],
  ["/admin/reports", { title: "Client Reports" }],
  ["/admin/reports/:id", { title: "Report Editor", backTo: () => "/admin/reports" }],
  ["/admin/audit-log", { title: "Audit Log" }],
  ["/admin/backups", { title: "Backups" }],
  ["/admin/vendors", { title: "Vendors" }],
  ["/admin/expenses/import", { title: "Import Expenses" }],
  ["/admin/expense-dashboard", { title: "Spend Dashboard" }],
  ["/admin/procurement", { title: "Procurement Planner" }],
  ["/admin/simulator", { title: "Cost Simulator" }],
  ["/admin/purchase", { title: "Purchase Requests" }],
  ["/admin/requisitions", { title: "Requisitions" }],
  ["/admin/review-flags", { title: "Review Flags" }],
  ["/admin/import-summary", { title: "Import Summary" }],
  ["/admin/fh-reports", { title: "FH Reports" }],
  ["/admin/upload-centre", { title: "Upload Centre" }],
  ["/admin/sites/:id/settings", { title: "Site Settings" }],
  ["/admin/coaching-mapping", { title: "Coaching → Course Mapping" }],
  ["/admin/courses/:id", { title: "Course Builder", backTo: () => "/training" }],
];

function backOneLevel(p) {
  const parts = p.split("/").filter(Boolean);
  parts.pop();
  return "/" + parts.join("/");
}

function matchPattern(pattern, pathname) {
  const pp = pattern.split("/").filter(Boolean);
  const ap = pathname.split("/").filter(Boolean);
  if (pp.length !== ap.length) return false;
  for (let i = 0; i < pp.length; i++) {
    if (pp[i].startsWith(":")) continue;
    if (pp[i] !== ap[i]) return false;
  }
  return true;
}

export function getRouteMeta(pathname) {
  for (const [pat, m] of META) {
    if (matchPattern(pat, pathname)) return m;
  }
  return { title: "" };
}

// Role landing (fallback back-target)
export function roleLanding(user) {
  const eff = user?.effective_role || user?.role;
  if (eff === "CLIENT") return "/client";
  if (eff === "PROCUREMENT_OFFICER") return "/procurement";
  if (eff === "OWNER" || eff === "HEAD") return "/command";
  return "/";
}

export function isLandingPath(pathname, user) {
  const meta = getRouteMeta(pathname);
  if (meta.landing) {
    // Only true landing FOR THIS ROLE — otherwise still show back.
    // e.g. /command is landing for Owner/Head, but if a PM navigates to /command manually
    // (they can), it's not their landing, so keep back button visible.
    return pathname === roleLanding(user);
  }
  return false;
}
